import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { faFileLines } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Alert from '../../../components/common/Alert';
import Spinner from '../../../components/common/Spinner';
import Modal from '../../../components/common/Modal';
import FormBuilderEditor from '../../../components/bpm/form/FormBuilderEditor';
import BpmFormService from '../../../services/BpmFormService';

const sanitizeFormKey = (name = '') => {
  let result = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  result = result.toLowerCase();
  result = result.replace(/[^a-z0-9]+/g, '_');
  result = result.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
  return result;
};

const BpmFormEditor = ({ formId = null, embedded = false, onClose = null, onDataChanged = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idFromQuery = query.get('id');
  const id = formId ?? idFromQuery;

  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [schemaContent, setSchemaContent] = useState('');
  const [formData, setFormData] = useState({
    id: null,
    formName: '',
    formKey: '',
    enabled: true,
  });

  useEffect(() => {
    if (!id) {
      return;
    }

    setLoading(true);
    BpmFormService.getById(id)
      .then((form) => {
        setFormData({
          id: form?.id || null,
          formName: form?.formName || '',
          formKey: form?.formKey || '',
          enabled: form?.enabled ?? true,
        });
        setSchemaContent(form?.formContent || '');
      })
      .catch((err) => {
        setError(err.message || t('common.error'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, t]);

  const handleSave = async () => {
    setError('');
    setSuccess('');

    if (!formData.formName?.trim()) {
      setError(t('bpmForm.errors.formNameRequired'));
      return;
    }

    if (!formData.formKey?.trim()) {
      setError(t('bpmForm.errors.formKeyRequired'));
      return;
    }

    const payload = {
      id: formData.id,
      formName: formData.formName.trim(),
      formKey: formData.formKey.trim(),
      formContent: schemaContent || '{"type":"default","components":[]}',
      enabled: formData.enabled ?? true,
    };

    setSaving(true);
    try {
      const response = await BpmFormService.create(payload);

      if (response?.id) {
        setFormData((prev) => ({ ...prev, id: response.id }));
      }
      setSuccess(t('bpmForm.success.saved'));
      if (onDataChanged) {
        onDataChanged();
      }
      setIsSaveModalOpen(false);
    } catch (err) {
      setError(err.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: '#fff', zIndex: 3000 }}>
        <Spinner overlay={true} size="large" message={t('common.loading')} />
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: '#fff',
        zIndex: 3000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #e5e5e5',
        }}
      >
        <h4 style={{ margin: 0 }}>
          <FontAwesomeIcon icon={faFileLines} /> {t('bpmForm.editorTitle')}
          {formData?.formName ? ` - ${formData.formName}` : ''}
        </h4>
        <button
          type="button"
          className="btn btn-danger btn-sm"
          style={{
            minWidth: 34,
            height: 34,
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6,
          }}
          onClick={() => {
            if (embedded && onClose) {
              onClose();
              return;
            }
            navigate('/bpm/form');
          }}
          aria-label={t('common.close') || 'Cerrar'}
        >
          <i className="fa fa-times" style={{ fontSize: 14 }}></i>
        </button>
      </div>

      <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
        <Alert type="error" message={error} onClose={() => setError('')} autoClose={5000} />
        <Alert type="success" message={success} onClose={() => setSuccess('')} autoClose={4000} />

        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-primary" onClick={() => setIsSaveModalOpen(true)} disabled={saving}>
            {saving ? t('common.processing') : t('common.save')}
          </button>
        </div>

        <FormBuilderEditor schemaContent={schemaContent} onSchemaChange={setSchemaContent} />
      </div>

      <Modal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        title={t('bpmForm.saveModalTitle') || 'Guardar formulario'}
        icon={faFileLines}
      >
        <div className="form-group">
          <label>{t('bpmForm.formName')}</label>
          <input
            type="text"
            className="form-control"
            value={formData.formName}
            onChange={(e) => {
              const formName = e.target.value;
              setFormData((prev) => ({
                ...prev,
                formName,
                formKey: sanitizeFormKey(formName),
              }));
            }}
          />
        </div>
        <div className="form-group">
          <label>{t('bpmForm.formKey')}</label>
          <input
            type="text"
            className="form-control"
            value={formData.formKey}
            onChange={(e) => setFormData((prev) => ({ ...prev, formKey: e.target.value }))}
          />
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setIsSaveModalOpen(false)}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? t('common.processing') : t('common.save')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default BpmFormEditor;
